import React, { useEffect, useState, useCallback } from "react";
import {
    Table,
    Form,
    Container,
    Row,
    Col,
    InputGroup,
    Button,
    Spinner,
    Badge,
} from "react-bootstrap";
import { getAuthToken } from "../../utils/auth";
import PaginationBar from "../common/PaginationBar";
import { useNavigate } from "react-router-dom"; // ✅ import
import { ITEMS_PER_PAGE } from "../../constants/appConstants";

const UserManagement = () => {
    const baseUrl = process.env.REACT_APP_API_URL;
    const token = getAuthToken();
    const navigate = useNavigate(); // ✅ init navigate

    const [users, setUsers] = useState([]);
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(0); // backend is 0-indexed
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);

    const itemsPerPage = ITEMS_PER_PAGE;

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);

            const url = new URL(`${baseUrl}/user/all`);
            url.searchParams.append("page", currentPage);
            url.searchParams.append("size", itemsPerPage);
            if (search.trim()) url.searchParams.append("query", search.trim());

            const res = await fetch(url, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            if (!res.ok) {
                console.error("Failed to fetch users:", res.statusText);
                return;
            }

            const result = await res.json();
            if (result.status) {
                setUsers(result.data || []);
                setTotalPages(result.pagination?.totalPages || 0);
            } else {
                setUsers([]);
            }
        } catch (err) {
            console.error("Error fetching users:", err);
        } finally {
            setLoading(false);
        }
    }, [baseUrl, token, itemsPerPage, currentPage, search]); // include both currentPage and search

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    useEffect(() => {
        setCurrentPage(0);
    }, [search]);

    const handleSearch = () => {
        setCurrentPage(0); // reset to first page when searching
    };

    const handleClear = () => {
        setSearch("");
        setCurrentPage(0);
    };

    // ✅ on row click
    const handleRowClick = (userId) => {
        navigate(`/admin/users/${userId}`);
    };

    return (
        <Container className="mt-4">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h3>User Management</h3>
                </Col>

                <Col xs="5">
                    <InputGroup>
                        <Form.Control
                            type="text"
                            placeholder="Search user..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                        />
                        <Button variant="primary" onClick={handleSearch}>
                            Search
                        </Button>
                        <Button variant="outline-secondary" onClick={handleClear}>
                            Clear
                        </Button>
                    </InputGroup>
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" />
                </div>
            ) : (
                <>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Phone number</th>
                                <th>Email</th>
                                <th>Full Name</th>
                                <th>Roles</th>
                                <th>Status</th>
                                <th>Account Type</th>
                                <th>Created</th>
                            </tr>
                        </thead>

                        <tbody>
                            {users.length > 0 ? (
                                users.map((u) => (
                                    <tr
                                        key={u.id}
                                        style={{ cursor: "pointer" }}
                                        onClick={() => handleRowClick(u.id)} // ✅ click to open details

                                    >
                                        <td>{u.id}</td>
                                        <td>
                                            {u.deletedAt ? (
                                                <span className="text-muted">{u.deletedPhoneNumber}</span>
                                            ) : (
                                                u.phoneNumber
                                            )}
                                        </td>
                                        <td>{u.email}</td>
                                        <td>
                                            {u.firstname} {u.lastname}
                                        </td>
                                        <td>{u.roles?.map((r) => r.name).join(", ")}</td>

                                        <td>
                                            {u.deletedAt ? (
                                                <Badge bg="danger" title={`Deleted ${new Date(u.deletedAt).toLocaleString()}`}>
                                                    Deleted {new Date(u.deletedAt).toLocaleDateString()}
                                                </Badge>
                                            ) : u.enabled ? "Active" : "Disabled"}
                                        </td>
                                        <td>{u.isPremium ? "Premium" : "Standart"}</td>
                                        <td>{new Date(u.createdDate).toLocaleString()}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center text-muted">
                                        No users found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    <PaginationBar
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    />
                </>
            )}
        </Container>
    );
};

export default UserManagement;
